
import { ArrowLeft, FileText, Clock, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import AdPanel from '@/components/ads/AdPanel';
import LegalFooter from '@/components/common/LegalFooter';

const CancellationRights = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const isGerman = currentLanguage === 'de';

  return (
    <div className="min-h-screen bg-carbon">
      <AdPanel />
      
      <div className="p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="sm"
              className="mr-3 text-ice-white hover:bg-ice-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/8c71c5e9-9b7a-44a8-8098-652cabdedf5f.png" 
                alt="ALPHA Logo" 
                className="w-8 h-8 mr-3"
              />
              <h1 className="text-2xl font-bold text-ice-white">
                {isGerman ? 'Widerrufsrecht' : 'Cancellation Rights'}
              </h1>
            </div>
          </div>

          <Card className="bg-carbon border-ice-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-ice-white">
                {isGerman ? 'Widerrufsrecht für Verbraucher' : 'Consumer Cancellation Rights'}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-ice-white/80 space-y-4">
              <div className="flex items-start space-x-3 p-4 bg-neon-red/10 rounded-lg border border-neon-red/20">
                <Clock className="w-5 h-5 text-neon-red flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-ice-white mb-1">
                    {isGerman ? '14-Tage Widerrufsrecht' : '14-Day Cancellation Period'}
                  </p>
                  <p>
                    {isGerman 
                      ? 'Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.'
                      : 'You have the right to cancel this contract within 14 days without giving any reason.'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">
                  {isGerman ? 'Widerrufsfrist' : 'Cancellation Period'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>
                  {isGerman 
                    ? 'Die Widerrufsfrist beträgt 14 Tage ab dem Tag des Vertragsabschlusses. Bei digitalen Inhalten, die sofort nach Vertragsabschluss bereitgestellt werden, beginnt die Frist mit dem Tag der Bestellung.'
                    : 'The cancellation period is 14 days from the day of contract conclusion. For digital content that is provided immediately after contract conclusion, the period begins on the day of the order.'
                  }
                </p>
                <p>
                  {isGerman
                    ? 'Um Ihr Widerrufsrecht auszuüben, müssen Sie uns mittels einer eindeutigen Erklärung über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren.'
                    : 'To exercise your right of cancellation, you must inform us by means of a clear statement of your decision to cancel this contract.'
                  }
                </p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">
                  {isGerman ? 'Widerrufsfolgen' : 'Effects of Cancellation'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>
                  {isGerman
                    ? 'Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, unverzüglich und spätestens binnen 14 Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist.'
                    : 'If you cancel this contract, we shall reimburse to you all payments received from you, without undue delay and in any event not later than 14 days from the day on which we are informed about your decision to cancel this contract.'
                  }
                </p>
                <p>
                  {isGerman
                    ? 'Für die Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart.'
                    : 'We will carry out such reimbursement using the same means of payment as you used for the initial transaction, unless you have expressly agreed otherwise.'
                  }
                </p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">
                  {isGerman ? 'Besonderheiten bei digitalen Inhalten' : 'Special Provisions for Digital Content'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-3">
                <p>
                  {isGerman
                    ? 'Haben Sie ausdrücklich zugestimmt, dass wir vor Ablauf der Widerrufsfrist mit der Ausführung der Dienstleistung beginnen, und haben Sie zur Kenntnis genommen, dass Sie durch Ihre Zustimmung mit Beginn der Ausführung der Dienstleistung Ihr Widerrufsrecht verlieren, erlischt Ihr Widerrufsrecht vorzeitig.'
                    : 'If you have expressly agreed that we begin the performance of services before the end of the cancellation period, and you have acknowledged that you lose your right of cancellation once the service has been fully performed, your right of cancellation expires early.'
                  }
                </p>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-ice-white">
                  <Mail className="w-5 h-5 text-neon-red" />
                  {isGerman ? 'So können Sie widerrufen' : 'How to Cancel'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-ice-white mb-2">
                      {isGerman ? 'Kontaktinformationen' : 'Contact Information'}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-neon-red" />
                        <span>support@alphamanonly.com</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-neon-red mt-0.5" />
                        <div>
                          <p>Kfir Yehuda</p>
                          <p>Fuggerstraße 9</p>
                          <p>10777 Berlin, Germany</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-ice-white mb-2">
                      {isGerman ? 'Widerrufsverfahren' : 'Cancellation Process'}
                    </h4>
                    <ol className="space-y-1 text-sm list-decimal list-inside">
                      <li>
                        {isGerman 
                          ? 'Senden Sie uns eine E-Mail mit Ihrer Widerrufserklärung'
                          : 'Send us an email with your cancellation declaration'
                        }
                      </li>
                      <li>
                        {isGerman
                          ? 'Geben Sie Ihre Kundennummer und Bestelldetails an'
                          : 'Include your customer number and order details'
                        }
                      </li>
                      <li>
                        {isGerman
                          ? 'Wir bestätigen den Erhalt Ihres Widerrufs'
                          : 'We confirm receipt of your cancellation'
                        }
                      </li>
                      <li>
                        {isGerman
                          ? 'Erstattung erfolgt innerhalb von 14 Tagen'
                          : 'Refund processed within 14 days'
                        }
                      </li>
                    </ol>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-ice-white/10">
                  <h4 className="font-semibold text-ice-white mb-2">
                    {isGerman ? 'Muster-Widerrufsformular' : 'Sample Cancellation Form'}
                  </h4>
                  <div className="bg-gray-800 p-4 rounded-lg text-sm">
                    <p className="mb-2">
                      {isGerman ? 'An: support@alphamanonly.com' : 'To: support@alphamanonly.com'}
                    </p>
                    <p className="mb-2">
                      {isGerman 
                        ? 'Hiermit widerrufe(n) ich/wir den von mir/uns abgeschlossenen Vertrag über die Erbringung der folgenden Dienstleistung:'
                        : 'I/We hereby give notice that I/We cancel my/our contract for the provision of the following service:'
                      }
                    </p>
                    <p className="mb-2">
                      {isGerman
                        ? 'ALPHA Premium Abonnement'
                        : 'ALPHA Premium Subscription'
                      }
                    </p>
                    <p className="mb-2">
                      {isGerman
                        ? 'Bestellt am: [Datum einfügen]'
                        : 'Ordered on: [Insert date]'
                      }
                    </p>
                    <p className="mb-2">
                      {isGerman
                        ? 'Name des/der Verbraucher(s): [Name einfügen]'
                        : 'Name of consumer(s): [Insert name]'
                      }
                    </p>
                    <p className="mb-2">
                      {isGerman
                        ? 'E-Mail-Adresse: [E-Mail einfügen]'
                        : 'Email address: [Insert email]'
                      }
                    </p>
                    <p>
                      {isGerman
                        ? 'Datum: [Datum einfügen]'
                        : 'Date: [Insert date]'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-carbon border-ice-white/10">
              <CardHeader>
                <CardTitle className="text-ice-white">
                  {isGerman ? 'Häufig gestellte Fragen' : 'Frequently Asked Questions'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-ice-white/80 space-y-4">
                <div>
                  <h4 className="font-semibold text-ice-white mb-2">
                    {isGerman 
                      ? 'Kann ich nach 14 Tagen noch kündigen?'
                      : 'Can I still cancel after 14 days?'
                    }
                  </h4>
                  <p className="text-sm">
                    {isGerman
                      ? 'Ja, Sie können Ihr Abonnement jederzeit kündigen. Das 14-Tage Widerrufsrecht gilt nur für die vollständige Erstattung. Nach dieser Frist können Sie zum Ende der Laufzeit kündigen.'
                      : 'Yes, you can cancel your subscription at any time. The 14-day cancellation right only applies to full refunds. After this period, you can cancel at the end of the billing cycle.'
                    }
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-ice-white mb-2">
                    {isGerman
                      ? 'Wie lange dauert die Erstattung?'
                      : 'How long does the refund take?'
                    }
                  </h4>
                  <p className="text-sm">
                    {isGerman
                      ? 'Erstattungen werden innerhalb von 14 Tagen nach Erhalt Ihres Widerrufs bearbeitet. Die Gutschrift auf Ihrem Konto kann je nach Zahlungsanbieter weitere 3-5 Werktage dauern.'
                      : 'Refunds are processed within 14 days of receiving your cancellation. The credit to your account may take an additional 3-5 business days depending on your payment provider.'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center py-8">
            <p className="text-ice-white/60 text-sm">
              {isGerman
                ? 'Diese Widerrufsbelehrung entspricht den deutschen und europäischen Verbraucherschutzbestimmungen.'
                : 'This cancellation policy complies with German and European consumer protection regulations.'
              }
            </p>
          </div>

          <LegalFooter />
        </div>
      </div>
    </div>
  );
};

export default CancellationRights;
